<script setup>
import {onBeforeMount, reactive, watch} from 'vue'
import {Routes, State, can, canUser, expression, getRoutePath, titlecase} from '@/paks/vu-app'

const SidebarWidth = 160

const page = reactive({
    autoHide: false,
    deviceId: State.context.deviceId,
    makingMenu: false,
    menu: [],
    mobile: State.app.mobile,
    needNewMenu: false,
    show: null,
    width: SidebarWidth,
})

watch(
    () => `${State.auth.ready}-${State.auth.role}-${State.app.needs.reload}-${State.sidebar.show}`,
    () => makeMenu(),
    { immediate: true }
)
watch(
    () => State.context,
    () => makeMenu(),
    {deep: true}
)
watch(
    () => getRoutePath(),
    () => State.sidebar.changePath()
)
watch(
    () => State.cache.dashboard,
    () => State.sidebar.changeDashboard,
    {deep: true}
)

onBeforeMount(() => {
    page.width = State.app.mobile ? window.innerWidth : SidebarWidth
    page.autoHide = State.config.features.nav?.sidebarHide
})

function makeMenu() {
    if (!State.auth.ready) return
    if (page.makingMenu) {
        page.needNewMenu = true
        return
    }
    page.makingMenu = true
    let nav = State.config.features.nav || {}
    page.deviceId = State.context.deviceId
    page.dark = State.app.dark

    //  State.sidebar.show holds the users preference for the sidebar state
    if (nav.sidebar) {
        page.show = State.sidebar.show || 'full'
        if (nav.sidebarHide && page.show == 'mini') {
            page.show = 'none'
        }
    } else {
        page.show = 'none'
    }
    State.sidebar.show = page.show
    page.menu.splice(0, page.menu.length)

    makeMenuItems(State.app.display.views)

    page.makingMenu = false
    if (page.needNewMenu) {
        page.needNewMenu = false
        makeMenu()
    }
}

function makeMenuItems(views) {
    for (let view of views) {
        if (!view) continue
        if (State.app.mock && !canUser({role: 'admin'}, view.role)) {
            continue
        }
        if (!can(view.role)) {
            continue
        }
        if (view.enable != null) {
            if (! expression(view.enable, State.context)) {
                continue
            }
        }
        if (view.menu) {
            let path = view.path
            if (path && view.path.indexOf(':') >= 0) {
                path = Routes.template(path, State.context)
            }
            page.menu.push({
                path: path,
                menu: view.menu,
                icon: view.icon,
                disabled: view.disabled,
            })
        }
        if (view.views) {
            makeMenuItems(view.views)
        }
    }
}

function isActive(path) {
    let rpath = getRoutePath()
    if (path == '/') {
        return rpath == '/' ? true : false
    }
    return rpath.indexOf(path) == 0
}

function clicked() {
    if (page.autoHide) {
        State.sidebar.show = 'none'
    }
}
</script>

<template>
    <v-navigation-drawer
        v-if="page.show && page.show != 'none'"
        clipped
        permanent
        app
        class="sidebar"
        :class="page.mobile ? 'mobile' : ''"
        :rail="page.show == 'mini' ? true : false"
        :width="page.width">
        <v-list class="pt-4 mt-5">
            <v-list-item
                v-for="item in page.menu"
                ripple
                :active="isActive(item.path)"
                :class="page.show"
                :key="item.menu"
                :to="item.path"
                :disabled="item.disabled"
                :id="'nav-' + item.menu.toLowerCase()"
                :value="item"
                @click="clicked">
                <template v-slot:prepend>
                    <v-tooltip
                        v-if="page.show == 'mini'"
                        right
                        nudge-left="4"
                        :text="item.menu"
                        :open-delay="100"
                        :open-hover="true">
                        <template v-slot:activator="{props}">
                            <v-icon :icon="item.icon" v-bind="props" size="large" />
                        </template>
                    </v-tooltip>
                    <v-icon :icon="item.icon" v-else size="large" />
                </template>
                <v-list-item-title v-if="page.show == 'full'">
                    {{ titlecase(item.menu) }}
                </v-list-item-title>
            </v-list-item>
            <v-spacer />
        </v-list>
    </v-navigation-drawer>
</template>

<style lang="scss">
@use 'sass:color';

.sidebar {
    position: fixed !important;
    background-color: rgb(var(--v-theme-sidebar)) !important;
    border-top: 1px solid rgba(var(--v-theme-border-lighten-1), 0.75) !important;
    border-right: 1px solid rgba(var(--v-theme-border), 0.75) !important;
    z-index: 100 !important;
    padding-top: 40px;
    .v-list {
        margin-top: 0 !important;
        padding: 0 !important;
    }
    .v-list-item {
        min-height: 60px !important;
        padding-right: 0 !important;
        border-bottom: 1px solid rgb(var(--v-theme-border-lighten-1));
        .v-list-item__action {
            margin-right: 20px !important;
        }
        .v-list-item__spacer {
            width: 16px !important;
        }
        a {
            min-height: 60px;
            .v-list-item__icon {
                font-size: 2em;
            }
        }
        &.mini {
            padding-right: 0 !important;
        }
        &:hover {
            background-color: color.adjust(gray, $lightness: 55%);
            z-index: 200;
        }
        &:hover.v-theme--dark {
            background-color: black;
        }
        .v-icon {
            color: rgb(var(--v-theme-primary));
            opacity: var(--v-high-emphasis-opacity)
        }
        .v-icon.v-theme--dark {
            color: rgb(var(--v-theme-accent-darken-1)) !important;
        }
    }
    .v-list-item--active,
    .side-menu-active {
        border-right: 2px solid rgb(var(--v-theme-accent)) !important;
        color: inherit !important;
        caret-color: inherit !important;
    }
}
.sidebar.mobile {
    top: 24px !important;
    height: 100% !important;
}
.v-tooltip > .v-overlay__content {
    background: rgb(var(--v-theme-surface));
    border: 1px solid rgb(var(--v-theme-border));
    color: rgb(var(--v-theme-on-surface));
}
</style>
